import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem, deleteItem } from '../fitness/fitnessApi';
import { useUserProfile, getNicknameForEmail } from '../UserProfileContext';
import VaultPicker from './VaultPicker';

const LIST = 'MissionTemplates';

const TIME_OFFSET_PRESETS = [
  { label: '2 days before', value: -2880 },
  { label: '1 day before', value: -1440 },
  { label: '2 hours before', value: -120 },
  { label: '1 hour before', value: -60 },
  { label: '45 min before', value: -45 },
  { label: '30 min before', value: -30 },
  { label: '15 min before', value: -15 },
];

function emptyTask() {
  return { name: '', timeOffset: -60, notes: '', vaultDocPath: '' };
}

function formatOffset(minutes) {
  const abs = Math.abs(minutes);
  if (abs >= 1440) return `${Math.round(abs / 1440)} day${abs >= 2880 ? 's' : ''} before`;
  if (abs >= 60) {
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m > 0 ? `${h}h ${m}m before` : `${h} hour${h > 1 ? 's' : ''} before`;
  }
  return `${abs} min before`;
}

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

function MissionTemplates({ getToken, userEmail, roles, procedures, onClose, onPickTemplate }) {
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('mine');
  const [nicknames, setNicknames] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [copying, setCopying] = useState(null);

  const { nickname } = useUserProfile();
  const pickMode = !!onPickTemplate;

  const fetchTemplates = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const items = await getItems(token, LIST);
      const mapped = items.map(it => ({
        id: it.id,
        name: it.Title || '',
        tasks: safeParse(it.Tasks),
        defaultRole: it.DefaultRole || '',
        createdBy: it.CreatedBy || '',
        createdDate: it.CreatedDate || '',
      }));
      setAllTemplates(mapped);

      const emails = [...new Set(mapped.map(t => t.createdBy).filter(Boolean))];
      const resolved = {};
      for (const email of emails) {
        resolved[email] = await getNicknameForEmail(token, email);
      }
      setNicknames(resolved);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const myTemplates = allTemplates.filter(t => t.createdBy.toLowerCase() === userEmail.toLowerCase());
  const familyTemplates = allTemplates.filter(t => t.createdBy.toLowerCase() !== userEmail.toLowerCase());
  const displayed = viewMode === 'mine' ? myTemplates : familyTemplates;

  function startNew() {
    setEditing({ name: '', tasks: [emptyTask()], defaultRole: '' });
  }

  function startEdit(tmpl) {
    setEditing({ ...tmpl, tasks: tmpl.tasks.length ? [...tmpl.tasks] : [emptyTask()] });
  }

  async function handleCopy(tmpl) {
    setCopying(tmpl.id);
    setError(null);
    try {
      const token = await getToken();
      await createItem(token, LIST, {
        Title: `${tmpl.name} (copy)`,
        Tasks: JSON.stringify(tmpl.tasks),
        DefaultRole: tmpl.defaultRole,
        CreatedBy: userEmail,
        CreatedDate: new Date().toISOString(),
      });
      await fetchTemplates();
      setViewMode('mine');
    } catch (err) {
      setError(err.message);
    } finally {
      setCopying(null);
    }
  }

  async function handleDelete(tmpl) {
    if (!window.confirm(`Delete template "${tmpl.name}"?`)) return;
    try {
      const token = await getToken();
      await deleteItem(token, LIST, tmpl.id);
      setAllTemplates(prev => prev.filter(t => t.id !== tmpl.id));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSave() {
    if (!editing.name.trim()) { setError('Template name is required'); return; }
    const validTasks = editing.tasks.filter(t => t.name.trim());
    if (validTasks.length === 0) { setError('Add at least one task'); return; }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const fields = {
        Title: editing.name.trim(),
        Tasks: JSON.stringify(validTasks),
        DefaultRole: editing.defaultRole,
        CreatedBy: editing.createdBy || userEmail,
        CreatedDate: editing.createdDate || new Date().toISOString(),
      };
      if (editing.id) {
        await updateItem(token, LIST, editing.id, fields);
      } else {
        await createItem(token, LIST, fields);
      }
      setEditing(null);
      await fetchTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function updateTask(index, field, value) {
    setEditing(prev => {
      const tasks = [...prev.tasks];
      tasks[index] = { ...tasks[index], [field]: value };
      return { ...prev, tasks };
    });
  }

  function addTask() {
    setEditing(prev => ({ ...prev, tasks: [...prev.tasks, emptyTask()] }));
  }

  function removeTask(index) {
    setEditing(prev => ({ ...prev, tasks: prev.tasks.filter((_, i) => i !== index) }));
  }

  function moveTask(from, to) {
    if (to < 0 || to >= editing.tasks.length) return;
    setEditing(prev => {
      const tasks = [...prev.tasks];
      const [moved] = tasks.splice(from, 1);
      tasks.splice(to, 0, moved);
      return { ...prev, tasks };
    });
  }

  // Drag state
  const [dragIdx, setDragIdx] = useState(null);
  const [vaultPickerForTask, setVaultPickerForTask] = useState(null); // task index
  const [procPickerForTask, setProcPickerForTask] = useState(null);

  if (loading) return <div className="MissionTpl"><p className="Centcom-events-loading">Loading templates...</p></div>;

  // Editor
  if (editing) {
    return (
      <div className="MissionTpl">
        <div className="MissionTpl-editor-header">
          <h2>{editing.id ? 'Edit Mission Template' : 'New Mission Template'}</h2>
          {onClose && <button className="Centcom-mission-exit" onClick={onClose}>&times;</button>}
        </div>
        {error && <p className="Fitness-error">{error}</p>}
        <input
          className="Fitness-input"
          type="text"
          placeholder="Template name (e.g. Swim Practice)"
          value={editing.name}
          onChange={e => setEditing(prev => ({ ...prev, name: e.target.value }))}
        />
        {roles.length > 0 && (
          <div className="MissionTpl-role-row">
            <span className="MissionTpl-role-label">Default Role:</span>
            {roles.map(r => (
              <button
                key={r.id}
                className={`Centcom-role-pill${editing.defaultRole === r.id ? '' : ' Centcom-role-pill--dimmed'}`}
                style={{
                  background: editing.defaultRole === r.id ? r.color : 'transparent',
                  color: editing.defaultRole === r.id ? '#fff' : r.color,
                  borderColor: r.color,
                }}
                onClick={() => setEditing(prev => ({ ...prev, defaultRole: r.id }))}
              >{r.name}</button>
            ))}
          </div>
        )}

        <div className="MissionTpl-tasks">
          {editing.tasks.map((task, i) => (
            <div
              key={i}
              className={`MissionTpl-task${dragIdx === i ? ' MissionTpl-task--dragging' : ''}`}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={e => { e.preventDefault(); if (dragIdx !== null && dragIdx !== i) { moveTask(dragIdx, i); setDragIdx(i); } }}
              onDragEnd={() => setDragIdx(null)}
            >
              <div className="MissionTpl-task-drag">&#9776;</div>
              <div className="MissionTpl-task-fields">
                <input
                  className="Fitness-input"
                  type="text"
                  placeholder="Task name"
                  value={task.name}
                  onChange={e => updateTask(i, 'name', e.target.value)}
                />
                <div className="MissionTpl-task-row">
                  <label className="MissionTpl-offset-label">
                    <span>When</span>
                    <select
                      className="Fitness-select"
                      value={TIME_OFFSET_PRESETS.some(p => p.value === task.timeOffset) ? task.timeOffset : 'custom'}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === 'custom') return;
                        updateTask(i, 'timeOffset', parseInt(v));
                      }}
                    >
                      {TIME_OFFSET_PRESETS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                      <option value="custom">Custom...</option>
                    </select>
                  </label>
                  {!TIME_OFFSET_PRESETS.some(p => p.value === task.timeOffset) && (
                    <label className="MissionTpl-offset-custom">
                      <span>Min before</span>
                      <input
                        className="Fitness-input Fitness-input--tiny"
                        type="number"
                        value={Math.abs(task.timeOffset)}
                        onChange={e => updateTask(i, 'timeOffset', -(parseInt(e.target.value) || 0))}
                      />
                    </label>
                  )}
                </div>
                <input
                  className="Fitness-input Fitness-input--small"
                  type="text"
                  placeholder="Notes (optional)"
                  value={task.notes}
                  onChange={e => updateTask(i, 'notes', e.target.value)}
                />
                <div className="MissionTpl-task-vault-row">
                  {task.vaultDocPath ? (
                    <span className="MissionTpl-task-vault">
                      Linked: {decodeURIComponent((task.vaultDocPath || '').split('/').pop())}
                      <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => updateTask(i, 'vaultDocPath', '')}>&times;</button>
                    </span>
                  ) : (
                    <>
                      <button className="MissionTpl-link-doc-btn" onClick={() => setVaultPickerForTask(i)}>Link Document</button>
                      {procedures && procedures.length > 0 && (
                        <button className="MissionTpl-link-doc-btn" onClick={() => setProcPickerForTask(i)}>Link Procedure</button>
                      )}
                    </>
                  )}
                  {task.procedureId && (
                    <span className="MissionTpl-task-vault">
                      Procedure: {(procedures || []).find(p => p.id === task.procedureId)?.name || task.procedureId}
                      <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => { updateTask(i, 'procedureId', ''); updateTask(i, 'procedureName', ''); }}>&times;</button>
                    </span>
                  )}
                </div>
              </div>
              <div className="MissionTpl-task-actions">
                <button className="Fitness-btn-icon" onClick={() => moveTask(i, i - 1)} disabled={i === 0}>&#9650;</button>
                <button className="Fitness-btn-icon" onClick={() => moveTask(i, i + 1)} disabled={i === editing.tasks.length - 1}>&#9660;</button>
                <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => removeTask(i)}>&times;</button>
              </div>
            </div>
          ))}
        </div>

        <button className="Fitness-btn Fitness-btn--secondary Fitness-btn--small" onClick={addTask}>+ Add Task</button>

        <div className="MissionTpl-editor-actions">
          <button className="Fitness-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Template'}
          </button>
          <button className="Fitness-btn Fitness-btn--secondary" onClick={() => { setEditing(null); if (!allTemplates.length && onClose) onClose(); }}>Cancel</button>
        </div>

        {vaultPickerForTask !== null && (
          <VaultPicker
            getToken={getToken}
            onPick={(file) => {
              updateTask(vaultPickerForTask, 'vaultDocPath', file.path);
              updateTask(vaultPickerForTask, 'vaultDocId', file.id);
              setVaultPickerForTask(null);
            }}
            onCancel={() => setVaultPickerForTask(null)}
          />
        )}
        {procPickerForTask !== null && procedures && (
          <div className="MissionTpl-picker-overlay" onClick={() => setProcPickerForTask(null)}>
            <div className="MissionTpl-picker" onClick={e => e.stopPropagation()}>
              <h3>Link a Procedure</h3>
              {procedures.map(p => (
                <button key={p.id} className="Centcom-postit-picker-item" onClick={() => {
                  updateTask(procPickerForTask, 'procedureId', p.id);
                  updateTask(procPickerForTask, 'procedureName', p.name);
                  setProcPickerForTask(null);
                }}>
                  <span className="Centcom-postit-picker-text">{p.name}</span>
                  <span className="Centcom-postit-picker-meta">{p.steps.length} steps</span>
                </button>
              ))}
              <button className="Fitness-btn Fitness-btn--small Fitness-btn--secondary" onClick={() => setProcPickerForTask(null)} style={{ marginTop: '0.5rem' }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="MissionTpl">
      <div className="MissionTpl-header">
        <h2>Mission Templates</h2>
        {onClose && <button className="Centcom-mission-exit" onClick={onClose}>&times;</button>}
      </div>
      {error && <p className="Fitness-error">{error}</p>}

      <div className="SwimTemplates-view-toggle">
        <button
          className={`SwimTemplates-toggle-btn${viewMode === 'mine' ? ' SwimTemplates-toggle-btn--active' : ''}`}
          onClick={() => setViewMode('mine')}
        >My Templates ({myTemplates.length})</button>
        <button
          className={`SwimTemplates-toggle-btn${viewMode === 'family' ? ' SwimTemplates-toggle-btn--active' : ''}`}
          onClick={() => setViewMode('family')}
        >Family ({familyTemplates.length})</button>
      </div>

      {viewMode === 'mine' && (
        <button className="Fitness-btn Fitness-btn--small" onClick={startNew}>+ New Template</button>
      )}

      {displayed.length === 0 && (
        <p className="Centcom-events-empty">
          {viewMode === 'mine' ? 'No templates yet. Create one to get started.' : 'No family templates shared yet.'}
        </p>
      )}

      <div className="MissionTpl-list">
        {displayed.map(tmpl => {
          const isOwner = tmpl.createdBy.toLowerCase() === userEmail.toLowerCase();
          const creatorName = nicknames[tmpl.createdBy] || tmpl.createdBy;
          const isExpanded = expandedId === tmpl.id;
          const roleName = roles.find(r => r.id === tmpl.defaultRole)?.name;
          const roleColor = roles.find(r => r.id === tmpl.defaultRole)?.color;
          return (
            <div key={tmpl.id} className="MissionTpl-card">
              <div className="MissionTpl-card-header" onClick={() => setExpandedId(isExpanded ? null : tmpl.id)}>
                <div className="MissionTpl-card-header-left">
                  {roleColor && <span className="MissionTpl-card-role-dot" style={{ background: roleColor }} />}
                  <div>
                    <span className="MissionTpl-card-name">{tmpl.name}</span>
                    <span className="MissionTpl-card-meta">
                      {tmpl.tasks.length} task{tmpl.tasks.length !== 1 ? 's' : ''}
                      {roleName && <> &middot; {roleName}</>}
                      {!isOwner && <> &middot; <em>{creatorName}</em></>}
                    </span>
                  </div>
                </div>
                <span className={`Centcom-event-chevron${isExpanded ? ' Centcom-event-chevron--open' : ''}`}>&#9660;</span>
              </div>
              {isExpanded && (
                <div className="MissionTpl-card-body">
                  {tmpl.tasks.map((t, i) => (
                    <div key={i} className="MissionTpl-card-task">
                      <span className="MissionTpl-card-task-offset">{formatOffset(t.timeOffset)}</span>
                      <span className="MissionTpl-card-task-name">{t.name}</span>
                      {t.notes && <span className="MissionTpl-card-task-notes">{t.notes}</span>}
                    </div>
                  ))}
                  <div className="MissionTpl-card-actions">
                    {pickMode && (
                      <button className="Fitness-btn Fitness-btn--small" onClick={() => onPickTemplate(tmpl)}>
                        Use This Template
                      </button>
                    )}
                    {isOwner && !pickMode && (
                      <>
                        <button className="Fitness-btn Fitness-btn--small" onClick={() => startEdit(tmpl)}>Edit</button>
                        <button className="Fitness-btn Fitness-btn--small Fitness-btn--danger" onClick={() => handleDelete(tmpl)}>Delete</button>
                      </>
                    )}
                    {!isOwner && !pickMode && (
                      <button className="Fitness-btn Fitness-btn--small" onClick={() => handleCopy(tmpl)} disabled={copying === tmpl.id}>
                        {copying === tmpl.id ? 'Copying...' : 'Copy to My Templates'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MissionTemplates;
